import { Link } from "react-router-dom";
import { Zap, Landmark, LineChart, BookOpen, ShieldCheck, BadgeCheck, Megaphone, Mail } from "lucide-react";
import { NewsletterCTA } from "@/components/NewsletterCTA";

const COVERAGE = [
  { icon: Zap, title: "Breaking news", desc: "The stories moving crypto right now — hacks, launches, ETF flows and market shocks, reported the moment they break." },
  { icon: LineChart, title: "Markets", desc: "Live prices, daily market briefs and the context behind every move in Bitcoin, Ethereum and beyond." },
  { icon: Landmark, title: "Policy & regulation", desc: "Bills, rulings and enforcement actions from Washington to Brussels to Seoul — tracked in one place." },
  { icon: BookOpen, title: "Explainers & research", desc: "Plain-English breakdowns and deep dives, so you understand the why — not just the what." },
];

const STANDARDS = [
  { icon: ShieldCheck, title: "Accuracy first", desc: "Every story is sourced and attributed. When the facts change, we update the story — and say so." },
  { icon: BadgeCheck, title: "Independent", desc: "We're reader-funded and independent. No token allegiances, no exchange partnerships shaping coverage." },
  { icon: Megaphone, title: "Sponsored means sponsored", desc: "Ads and press releases are always clearly labeled. Paid placement never wears the costume of news." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] mb-3">About NewsLayer</p>
      <h1 className="font-news font-black text-white text-4xl sm:text-5xl leading-tight max-w-3xl">
        Crypto moves fast. We keep you a layer ahead.
      </h1>
      <p className="text-gray-300 text-lg leading-relaxed mt-6 max-w-3xl">
        NewsLayer was built on a simple frustration: keeping up with crypto meant juggling a dozen
        tabs, a noisy feed and headlines written for insiders. We wanted one place where the
        signal rises above the noise — where breaking news, market moves and regulation actually
        make sense together.
      </p>
      <p className="text-gray-400 leading-relaxed mt-4 max-w-3xl">
        So that's what we publish: fast, sourced, readable coverage of crypto, Web3, DeFi and the
        policy decisions shaping them — around the clock, from wherever the story breaks.
      </p>

      <section className="mt-14 sm:mt-16" data-testid="about-coverage">
        <h2 className="font-news font-bold text-white text-lg md:text-lg mb-6">What we cover</h2>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {COVERAGE.map((c) => (
            <div key={c.title} className="border border-[#7C3AED]/40 rounded-xl p-5 bg-[#0d0d0d]">
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[#7C3AED]/20 text-[#A78BFA]">
                  <c.icon className="h-[18px] w-[18px]" />
                </span>
                <h3 className="font-news font-bold text-white">{c.title}</h3>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 sm:mt-16" data-testid="about-standards">
        <h2 className="font-news font-bold text-white text-lg md:text-lg mb-6">How we hold ourselves accountable</h2>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
          {STANDARDS.map((s) => (
            <div key={s.title} className="border border-white/10 rounded-xl p-5">
              <s.icon className="h-5 w-5 text-[#00E5FF] mb-3" />
              <h3 className="font-news font-bold text-white mb-1.5">{s.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-sm mt-6 max-w-3xl">
          Spotted an error? Tell us and we'll fix it fast — corrections are part of the job, not an
          embarrassment.
        </p>
      </section>

      <section className="mt-14 sm:mt-16" data-testid="about-community">
        <h2 className="font-news font-bold text-white text-lg md:text-lg mb-4">Built with our readers</h2>
        <p className="text-gray-400 leading-relaxed max-w-3xl">
          NewsLayer is more than a feed. Approved authors publish their own reporting through our{" "}
          <Link to="/author-program" className="text-[#00E5FF] hover:underline">Author Program</Link>,
          readers shape what trends, and our{" "}
          <Link to="/premium" className="text-[#00E5FF] hover:underline">Premium members</Link>{" "}
          keep the newsroom independent. You can read us in ten languages, follow the{" "}
          <Link to="/briefs" className="text-[#00E5FF] hover:underline">Daily Market Brief</Link>,
          or track every regulation on the{" "}
          <Link to="/regulation-tracker" className="text-[#00E5FF] hover:underline">Reg Tracker</Link>.
        </p>
      </section>

      <section className="mt-14 sm:mt-16 border border-white/10 rounded-xl p-6 sm:p-8 bg-[#0d0d0d]" data-testid="about-contact">
        <h2 className="font-news font-bold text-white text-lg md:text-lg mb-2">Talk to us</h2>
        <p className="text-gray-400 text-sm leading-relaxed max-w-2xl mb-5">
          Tips, corrections, partnerships or just a strong opinion about a headline — we read
          everything.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <a href="mailto:newslayerapp@gmail.com" data-testid="about-email-link"
            className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md px-4 py-2 text-sm font-medium transition-colors">
            <Mail className="h-4 w-4" /> newslayerapp@gmail.com
          </a>
          <a href="https://x.com/newslayerapp" target="_blank" rel="noreferrer" data-testid="about-x-link"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-300 hover:text-white rounded-md px-4 py-2 text-sm font-medium transition-colors">
            @newslayerapp on X
          </a>
          <a href="https://t.me/newslayerapp" target="_blank" rel="noreferrer" data-testid="about-telegram-link"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-300 hover:text-white rounded-md px-4 py-2 text-sm font-medium transition-colors">
            Telegram channel
          </a>
        </div>
      </section>

      <section className="mt-14 sm:mt-16" data-testid="about-newsletter">
        <h2 className="font-news font-bold text-white text-lg md:text-lg mb-2">Start your morning a layer ahead</h2>
        <p className="text-gray-400 text-sm mb-5 max-w-2xl">
          One daily email with every story that matters. No spam, unsubscribe anytime.
        </p>
        <div className="max-w-sm">
          <NewsletterCTA sourceType="about" compact />
        </div>
      </section>
    </div>
  );
}
